import { Box, Text, Flex, useToast } from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import {
  GetFundingVerificationPipelineRunsDocumentGQLType,
  useAdjudicateFundingRequestVerificationReportMutation,
} from '../requests/requests.gql.generated';
import { VerificationReportTakeActionButton } from './VerificationReportTakeActionButton';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { TextareaFormField } from 'packages/common-frontend/components/FormField';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from 'packages/common-frontend/hooks/useUiVisibility';
import { GetFundingRequestsWithEventDetailsDocumentGQLType } from 'packages/funding-requests/requests/requests.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
type VerificationApproveDenyActionProps = {
  verificationReportId: string;
  creditFacilityId: string;
};
export const VerificationApproveDenyAction = ({
  verificationReportId,
  creditFacilityId,
}: VerificationApproveDenyActionProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const { navigator } = useVerificationOptions();
  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId,
  });

  const [
    adjudicateFundingRequestVerificationReport,
  ] = useAdjudicateFundingRequestVerificationReportMutation({
    onCompleted: (data) => {
      if (data && data.adjudicateFundingRequestVerificationReport) {
        toast({
          ...successToastConfig,
          id: 'adjudicateFundingRequestVerificationReport',
          title: data.adjudicateFundingRequestVerificationReport.response,
        });
        navigator(navigate).reset();
      }
    },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'adjudicateFundingRequestVerificationReport',
        title:
          'Could not verify funding request at this time. Please try again.',
      });
    },
    refetchQueries: [
      GetFundingRequestsWithEventDetailsDocumentGQLType,
      GetFundingVerificationPipelineRunsDocumentGQLType,
    ],
    awaitRefetchQueries: true,
  });

  const [pendingOperation, setPendingOperation] = useState<
    'approve' | 'reject' | 'none' | 'requestRemediation'
  >('none');

  const onClickReject = useCallback(
    async (
      formProps: FormikProps<{
        note: string;
      }>,
    ) => {
      try {
        setPendingOperation('reject');
        await adjudicateFundingRequestVerificationReport({
          variables: {
            verificationReportId,
            action: 'reject',
            note: formProps.values.note,
          },
        });
      } finally {
        setPendingOperation('none');
      }
    },
    [adjudicateFundingRequestVerificationReport, verificationReportId],
  );

  const onClickRequestRemediation = useCallback(
    async (
      formProps: FormikProps<{
        note: string;
      }>,
    ) => {
      try {
        setPendingOperation('requestRemediation');
        await adjudicateFundingRequestVerificationReport({
          variables: {
            verificationReportId,
            action: 'requestRemediation',
            note: formProps.values.note,
          },
        });
      } finally {
        setPendingOperation('none');
      }
    },
    [adjudicateFundingRequestVerificationReport, verificationReportId],
  );

  const onClickApprove = useCallback(
    async (
      formProps: FormikProps<{
        note: string;
      }>,
    ) => {
      try {
        setPendingOperation('approve');
        await adjudicateFundingRequestVerificationReport({
          variables: {
            verificationReportId,
            action: 'approve',
            note: formProps.values.note,
          },
        });
      } finally {
        setPendingOperation('none');
      }
    },
    [adjudicateFundingRequestVerificationReport, verificationReportId],
  );

  const initialValues = {
    note: '',
  };

  const validationSchema = Yup.object().shape({
    note: Yup.string().max(250, 'You may only enter up to 250 characters'),
  });

  return (
    <Box>
      <Text fontWeight="semibold">
        Finley&apos;s calc agent outputs will be stored for record-keeping
        purposes, including insights. The requested funding amount (draw,
        recycle or paydown) is included in the servicer report.
      </Text>
      <Box h="40px" />
      <Text fontWeight="semibold">Notes</Text>
      <Box h="16px" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {}}
      >
        {(formProps) => (
          <Form>
            <Box backgroundColor="white" mb="40px">
              <TextareaFormField
                propName="note"
                formProps={formProps}
                placeholder="If requesting remediation, include a note for the borrower."
              />
            </Box>
            <Flex>
              <Flex ml="auto" style={{ gap: '32px' }}>
                <VerificationReportTakeActionButton
                  pendingOperation={pendingOperation}
                  onClickConfirmAction={() => onClickReject(formProps)}
                  headerText="Confirm Funding Rejection"
                  bodyText="Are you sure you want to reject this funding request? Once rejected, it cannot be undone. The borrower will need to resubmit this funding request."
                  confirmActionButtonText="Reject"
                  mainActionButtonStyle="outline"
                />
                {facilityUiVisibility.verification
                  .showRequestRemediationAction && (
                  <VerificationReportTakeActionButton
                    pendingOperation={pendingOperation}
                    onClickConfirmAction={() =>
                      onClickRequestRemediation(formProps)
                    }
                    headerText="Confirm Remediation Request"
                    bodyText="Are you sure you want to request remediation for this issue? This action will notify the appropriate parties for further action. Include a note for the borrower."
                    confirmActionButtonText="Request Remediation"
                    mainActionButtonStyle="outline"
                  />
                )}
                <VerificationReportTakeActionButton
                  pendingOperation={pendingOperation}
                  onClickConfirmAction={() => onClickApprove(formProps)}
                  headerText="Approve Funding Request"
                  bodyText="Are you sure you want to approve this funding request? Once approved, it cannot be undone."
                  confirmActionButtonText="Approve Funding Request"
                  mainActionButtonStyle="fill"
                />
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
      <Box h="40px" />
    </Box>
  );
};
